// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../common/api';
import config from '../../../share/config';
import handler from '../../../share/util/apiResultValidator';
import util from '../../../share/util/utils';
import loading from '../../../share/components/vue/ProcessingIcon';
import cstmPaging from '../../../share/components/vue/Pagination';
import cstmTable from '../../../share/components/vue/PaginationTable';
import cstmCard from "../../../share/components/vue/CustomCard";
import svgButton from '../../../share/components/vue/SVGButton';
import exam from '../../../share/util/inputValidator';
import constants from '../../../common/nodejs/constants';
import i18nResourceLoader from '../../../i18n/geti18nLoad';

const SEARCH_YEAR_RANGE = 10;

// 大量データは vue 管理外で保持（ IE メモリリーク対策）
let projectsBuff = [];
let filteredBuff = [];

export default {
  name: "diagProjectList",
  data() {
    return {
      processing: false,
      isSearchFieldOpened: true,
      selectedYear: null,
      searchYearOptions: [''],
      filledVin: '',
      filledProjectId: '',
      selectedBelongs: null,
      selectedVehicle: null,
      selectedClass: null,
      selectedMy: null,
      selectedDatasource: null,
      filledComment: '',
      filterBelongsOptions: ['-'],
      filterVehicleOptions: ['-'],
      filterClassOptions: ['-'],
      filterMyOptions: ['-'],
      projectTableColumns: [
        {
          name: 'id', label: 'message.header_measured_data_list_project_id', originalSortFunc: (param) => {
            // ソート時だけ数値に変換して比較する
            return util.sortItems(param.data, param.sortKey, param.sortDir, true);
          }
        },
        { name: 'registered_at', label: 'message.header_measured_data_list_registered_year' },
        { name: 'belongs', label: 'message.header_measured_data_list_belongs' },
        { name: 'vin', label: 'message.header_measured_data_list_vin' },
        { name: 'vehicle_name', label: 'message.header_measured_data_list_vehicle_name' },
        { name: 'model_type', label: 'message.header_measured_data_list_my' },
        { name: 'comment', label: 'message.header_measured_data_list_comment' },
      ],
      projectsTable: [],
      maxItemsPerPage: Number(process.env.VUE_APP_PROJECT_PAGE_DISPLAY_COUNT),
      currentPage: 0,
      totalProjectsLength: 0,
      runPagingProcess: false,
      alertObject: {},
      currentEnvironment: "master"
    };
  },

  watch: {
    // noop
  },

  components: {
    'processing-view': loading,
    'cstm-card': cstmCard,
    'svg-button': svgButton,
    'cstm-tbl': cstmTable,
    'cstm-paging': cstmPaging
  },

  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('cache', ['cacheDiagProjectList', 'cacheProjectDetails', 'cacheProjectDataSource']),

    updateProjectList: async function (query = null) {
      this.processing = true;

      const queryWithDefaults = { ...query };

      // 初期表示の場合は sign_in_id を追加
      if (this.isAllConditionsEmpty()) {
        queryWithDefaults.sign_in_id = this.userId;
      }

      const params = queryWithDefaults;

      const res = await api.getParamCall(config.DIAG, '/projects', params);
      this.processing = false;
      handler.validate(handler.validateTypes.all, res, this, null, () => {
        projectsBuff = [];
        this.showFirstPage();
        this.totalProjectsLength = res.data.projects.length;
        this.clearFilterCondition();
        this.stackProjectsToBuff(res.data.projects);
        this.updateFilterOptions();
        this.updateFilter();
        if (util.hasLastEvaluatedKey(res.data)) {
          this.runPagingProcess = true;
          const key = JSON.stringify(res.data.last_evaluated_key);
          this.startPagingProcess(params, key);
        }
      }, (result) => {
        if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
      });
    },

    // API から取得したプロジェクト一覧を Frontend 側のバッファリング変数に追加
    stackProjectsToBuff: function (projects) {
      for (const p of projects) {
        p.measuredId = p.registered_at;
        // UNIX => YYYYMMDD HH:MM 変換
        p.registered_at = util.timeConverter(p.registered_at, null, 'min');
        p.model_type = p.model_year || p.classification;
        projectsBuff.push(p);
      }

      // 基本的にはプロジェクトID順にソートする。
      projectsBuff = util.sortItems(projectsBuff, 'id', config.DESC, true);
    },

    // 取得できているプロジェクト全体から、現在のページ表示するものだけをフィルタ
    filterPerPageSize: function (list) {
      const ret = list.filter((row, index) => {
        const start = (this.currentPage - 1) * this.maxItemsPerPage;
        const end = this.currentPage * this.maxItemsPerPage;
        return index >= start && index < end;
      });
      return ret;
    },

    // 一度の API アクセスで取得できるプロジェクト件数を超えてる場合は再取得
    startPagingProcess: async function (query, key) {
      const params = { ...query, last_evaluated_key: key };
      while (this.runPagingProcess) {
        const res = await api.getParamCall(config.DIAG, '/projects', params);
        handler.validate(handler.validateTypes.all, res, this, null, () => {
          this.stackProjectsToBuff(res.data.projects);
          this.updateFilterOptions();
          this.updateFilter();
          // 次のデータが存在するならループを継続、存在しないならページング処理を終了 
          if (util.hasLastEvaluatedKey(res.data)) {
            params.last_evaluated_key = JSON.stringify(res.data.last_evaluated_key);
          } else {
            this.runPagingProcess = false;
            return;
          }
        }, (result) => {
          // API アクセスエラーなら途中で処理を終了
          this.runPagingProcess = false;
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
          return;
        });
      }
    },

    // プロジェクト検索の実施
    searchProject: async function () {
      this.processing = true;

      // ミラーサイト → マスターサイト のように表示サイトと異なるデータソースを検索した場合、データソース側のリソースファイルをS3から取得する
      this.currentEnvironment = process.env.VUE_APP_BACKUP_TYPE ? "mirror" : "master";
      // Stg1,Stg2などはミラー環境がないのでリソースを取得しない
      if (!util.isCloudFrontUrl() && (this.selectedDatasource !== this.currentEnvironment)) {
        const region = util.getPackJsonRegion(this.selectedDatasource);
        i18nResourceLoader.start(region, this.language);
      }

      // すべて未入力・未選択の場合
      if (this.isAllConditionsEmpty()) {
        const query = {
          region_info: this.selectedDatasource
        };
        this.cacheProjectDataSource(this.selectedDatasource);
        this.clearAlert();
        this.updateProjectList(query);
        return;
      }

      // 通常のバリデーション処理
      if (!this.isValidQuery()) {
        this.processing = false;
        return;
      }
      const query = {};
      if (this.filledProjectId) {
        query.project_id = Number(this.filledProjectId.trim());
      } else {
        query.registered_at = this.selectedYear;
        query.vin = this.filledVin.trim();
      }

      // リージョン情報設定
      query.region_info = this.selectedDatasource;
      this.cacheProjectDataSource(this.selectedDatasource);

      this.updateProjectList(query);
    },

    // プロジェクト検索時のクエリ条件が適正かどうかを検証
    isValidQuery: function () {
      this.clearAlert();
      if (!this.selectedYear && !this.filledProjectId.trim()) {
        this.alertObject.registeredAt = { valid: false, msg: 'message.alert_validate_partial_required' };
        this.alertObject.projectId = { valid: false, msg: 'message.alert_validate_partial_required' };
        return false;
      }
      if (this.selectedYear) {
        // VINの必須と最大数チェックを実施する
        this.alertObject.vin = exam.excuteValidateCommons(this.filledVin.trim(), true, 0, config.VEHICLEINFO_VIN_MAX_LEN);
        if (this.alertObject.vin.valid === false) {
          return false;
        }
      }
      return true;
    },

    // 条件エリアの項目がすべて未選択、未入力かどうかを検証
    isAllConditionsEmpty: function () {

      const isEmptyYear = !this.selectedYear;
      const isEmptyVin = !this.filledVin || this.filledVin.trim() === '';
      const isEmptyProjectId = !this.filledProjectId || this.filledProjectId.trim() === '';

      // すべて未入力・未選択の場合
      if (isEmptyYear && isEmptyVin && isEmptyProjectId) {
        return true;
      } else {
        return false;
      }
    },

    // フィルタ条件のプルダウンオプションを更新
    updateFilterOptions: function () {
      this.filterBelongsOptions = ['-'];
      this.filterVehicleOptions = ['-'];
      this.filterClassOptions = ['-'];
      this.filterMyOptions = ['-'];
      projectsBuff.forEach(prj => {
        if (this.filterBelongsOptions.findIndex(e => e === prj.belongs) < 0) {
          if (prj.belongs) this.filterBelongsOptions.push(prj.belongs);
        }
        if (this.filterVehicleOptions.findIndex(e => e === prj.vehicle_name) < 0) {
          if (prj.vehicle_name) this.filterVehicleOptions.push(prj.vehicle_name);
        }
        if (this.filterClassOptions.findIndex(e => e === prj.classification) < 0) {
          if (prj.classification) this.filterClassOptions.push(prj.classification);
        }
        if (this.filterMyOptions.findIndex(e => e === prj.model_year) < 0) {
          if (prj.model_year) this.filterMyOptions.push(prj.model_year);
        }
      });
    },

    // 指定条件でフィルタ
    updateFilter: function () {
      filteredBuff = projectsBuff
        .filter(prj => { return (this.selectedBelongs !== '-') ? this.selectedBelongs === util.trimStr(prj.belongs) : true; })
        .filter(prj => { return (this.selectedVehicle !== '-') ? this.selectedVehicle === util.trimStr(prj.vehicle_name) : true; })
        .filter(prj => { return (this.selectedClass !== '-') ? this.selectedClass === prj.classification : true; })
        .filter(prj => { return (this.selectedMy !== '-') ? this.selectedMy === prj.model_year : true; })
        .filter(prj => { return (this.filledComment) ? util.trimStr(prj.comment).indexOf(this.filledComment) !== -1 : true; });
      this.totalProjectsLength = filteredBuff.length;
      // フィルター実施後は初期ページに戻す。
      this.showFirstPage();
      this.projectsTable = this.filterPerPageSize(filteredBuff);
    },

    // フィルタ条件をクリア
    clearFilterCondition: function () {
      this.showFirstPage();
      this.selectedBelongs = this.filterBelongsOptions[0];
      this.selectedVehicle = this.filterVehicleOptions[0];
      this.selectedClass = this.filterClassOptions[0];
      this.selectedMy = this.filterMyOptions[0];
      this.filledComment = '';
      this.updateFilter();
    },

    // テーブルページの切り替え
    switchTablePage: function (page) {
      this.$refs.project_tbl.scrollToTop();
      this.currentPage = page;
      this.projectsTable = this.filterPerPageSize(filteredBuff);
    },

    // プロジェクト詳細画面に遷移
    goToProjectDetail: function (row) {
      // ページング処理による全件取得中は詳細画面に遷移させない
      if (this.runPagingProcess) return;
      // プロジェクト一覧をキャッシュ
      this.cacheDiagProjectList({
        search: {
          year: this.selectedYear,
          vin: this.filledVin,
          projectId: this.filledProjectId,
          datasource: this.selectedDatasource,
        },
        filter: {
          belongs: this.selectedBelongs,
          vehicle: this.selectedVehicle,
          classification: this.selectedClass,
          my: this.selectedMy,
          comment: this.filledComment
        },
        list: projectsBuff
      });
      // 選択されたプロジェクト詳細の概要情報だけをキャッシュ
      this.cacheProjectDetails({ information: row });
      this.$router.push({ name: 'diagProjectDetails' });
    },

    // 最初のページを表示
    showFirstPage: function () {
      this.currentPage = 1;
      if (this.$refs.project_paging) this.$refs.project_paging.initializePagenation();
    },

    clearAlert: function () {
      this.alertObject = {
        registeredAt: { valid: null, msg: '' },
        vin: { valid: null, msg: '' },
        projectId: { valid: null, msg: '' }
      };
    },
  },

  computed: {
    ...mapState({
      datasourceList: state => state.fileResources.datasource,
      userId: state => state.userProperty.userId,
      cachedBuff: state => state.cache.projectListBuff,
      language: state => state.systems.language,
    }),

    filledProjectIdDisabled: {
      get: function () {
        if (!this.selectedYear) {
          return false;
        }
        return true;
      },
    },

    filterClassdDisabled: {
      get: function () {
        if (this.selectedMy) {
          if (this.selectedMy !== "-") {
            return true;
          }
        }
        return false;
      },
    },

    filterMyDisabled: {
      get: function () {
        if (this.selectedClass) {
          if (this.selectedClass !== "-") {
            return true;
          }
        }
        return false;
      },
    }

  },

  async created() {
    this.clearAlert();
  },

  mounted() {
    // 初期の検索条件の設定（現在の年＆現在の利用者氏名）
    const currentYear = new Date().getFullYear();
    this.searchYearOptions.push(String(currentYear));

    // ミラーサイトの判定
    this.selectedDatasource = process.env.VUE_APP_BACKUP_TYPE ? constants.MAINTENANCE_ACTIVE_ENV_MIRROR : constants.MAINTENANCE_ACTIVE_ENV_DEFAULT;

    for (let i = 1; i <= SEARCH_YEAR_RANGE; i++) {
      this.searchYearOptions.push(String(currentYear - i));
    }
    if (this.cachedBuff.search && this.cachedBuff.filter && this.cachedBuff.list) {
      // キャッシュ済み一覧があれば初期表示として使用
      this.selectedYear = this.cachedBuff.search.year || null;
      this.filledVin = this.cachedBuff.search.vin || '';
      this.filledProjectId = this.cachedBuff.search.projectId || '';
      projectsBuff = this.cachedBuff.list;
      this.showFirstPage();
      this.totalProjectsLength = projectsBuff.length;
      this.updateFilterOptions();
      this.selectedBelongs = this.cachedBuff.filter.belongs;
      this.selectedVehicle = this.cachedBuff.filter.vehicle;
      this.selectedClass = this.cachedBuff.filter.classification;
      this.selectedMy = this.cachedBuff.filter.my;
      this.filledComment = this.cachedBuff.filter.comment;
      this.selectedDatasource = this.cachedBuff.search.datasource;
      this.updateFilter();
    } else {

      const query = {};
      query.region_info = this.selectedDatasource;
      this.cacheProjectDataSource(this.selectedDatasource);
      this.updateProjectList(query);

    }
    this.cacheProjectDetails(null);
  },

  beforeUnmount() {
    projectsBuff = [];
    filteredBuff = [];
    this.runPagingProcess = false;
  },

  // i18nリソースの取得完了まで画面遷移しないようにする
  async beforeRouteLeave(to, from, next) {
    this.processing = true;
    await i18nResourceLoader.wait();
    this.processing = false;
    next();
  }
};